/**
 * alertshub#5 - is a submitted screener a risk? The Form Builder's own scoring (credifyfbs
 * backend/lib/scoring/score.ts, itself a port of app.html's runtime scorer), on the answers the
 * EHR stores: option LABEL text, not the Form Builder's option indexes.
 *
 * A scoring section raises a risk when:
 *  - its alert fires: a critical answer (PHQ-9 item 9 above "Not at all", a C-SSRS "Yes" on
 *    items 4-6) or a total at or over the alert's threshold (ACE 4+, PC-PTSD-5 3+, CRAFFT 2+), or
 *  - its total lands in its top band and that band is high severity: PHQ-9 / GAD-7 "Severe",
 *    PCL-5 "Probable PTSD", a C-SSRS positive screen, AUDIT "Possible dependence".
 * The Form Builder's built-in PHQ-9, GAD-7 and PCL-5 carry bands but no total threshold, so the
 * second rule is what makes their severe band an alert. The credifyfbs copy (lib/scoring/risk.ts)
 * applies the same two rules to a public-link submission.
 *
 * Pure (no I/O), so it is testable.
 */
const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};
const norm = (v) => String(v ?? '')
    .trim()
    .toLowerCase();
const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
/** An answer's option index: a label (what the EHR stores) or, for a raw index, a number. */
function optionIndex(field, answer) {
    if (answer === undefined || answer === null || answer === '')
        return -1;
    if (typeof answer === 'number')
        return Number.isInteger(answer) ? answer : -1;
    const want = norm(answer);
    return field.options.findIndex((o) => norm(o) === want);
}
/** A checkboxes answer's ticked options: a list, one option, or the list joined with ", ". */
function ticked(field, a) {
    if (Array.isArray(a))
        return a;
    if (a === undefined || a === null || a === '')
        return [];
    if (typeof a !== 'string' || optionIndex(field, a) >= 0)
        return [a];
    return a.split(', ');
}
/** Mirrors score.ts scoreRuleMatch (number / date / text rules; first match wins). */
function ruleMatches(type, value, r) {
    const empty = value == null || value === '' || (Array.isArray(value) && value.length === 0);
    if (r.op === 'empty')
        return empty;
    if (r.op === 'filled')
        return !empty;
    if (r.op === 'always')
        return true;
    if (empty)
        return false;
    if (type === 'number' || type === 'range' || type === 'rating') {
        const n = Number(value);
        if (Number.isNaN(n))
            return false;
        const a = Number(r.value);
        const b = Number(r.value2);
        switch (r.op) {
            case 'eq':
                return n === a;
            case 'neq':
                return n !== a;
            case 'gt':
                return n > a;
            case 'lt':
                return n < a;
            case 'gte':
                return n >= a;
            case 'lte':
                return n <= a;
            case 'between':
                return n >= Math.min(a, b) && n <= Math.max(a, b);
        }
        return false;
    }
    const s = norm(value);
    const want = norm(r.value);
    if (r.op === 'eq')
        return s === want;
    if (r.op === 'contains')
        return s.includes(want);
    if (r.op === 'starts')
        return s.startsWith(want);
    return false;
}
/** One field's points (score.ts scoreField), from the answers keyed by field id. */
export function fieldScore(fieldId, field, answers) {
    if (!field)
        return 0;
    const a = answers[fieldId];
    switch (field.type) {
        case 'radio':
        case 'select': {
            const i = optionIndex(field, a);
            return i >= 0 ? num(field.scores[i]) : 0;
        }
        case 'checkboxes': {
            return ticked(field, a).reduce((sum, x) => {
                const i = optionIndex(field, x);
                return sum + (i >= 0 ? num(field.scores[i]) : 0);
            }, 0);
        }
        case 'toggle':
        case 'checkbox':
            if (a === undefined || a === null || a === '')
                return 0;
            return a === true || norm(a) === 'true' || norm(a) === 'yes'
                ? num(field.scores[0])
                : num(field.scores[1]);
        case 'matrix': {
            // the EHR keeps a matrix answer per row, `<field id>::<row index>`, as the column's label
            let sum = 0;
            const rows = isObj(a) ? Object.values(a) : [];
            for (const [k, v] of Object.entries(answers))
                if (k.startsWith(`${fieldId}::`))
                    rows.push(v);
            for (const v of rows) {
                const i = optionIndex(field, v);
                if (i >= 0)
                    sum += num(field.scores[i]);
            }
            return sum;
        }
        default:
            for (const r of field.rules ?? [])
                if (ruleMatches(field.type, a, r))
                    return num(r.points);
            return 0;
    }
}
/** Does a critical item's answer hit one of its options (score.ts answerHits)? */
function criticalHit(c, cfg, answers) {
    const field = cfg.fields[c.fieldId];
    const opts = (c.options ?? []).map(Number);
    if (!field || !opts.length)
        return false;
    const a = answers[c.fieldId];
    if (field.type === 'checkboxes')
        return ticked(field, a).some((x) => opts.includes(optionIndex(field, x)));
    if (field.type === 'toggle' || field.type === 'checkbox') {
        if (a === undefined || a === null || a === '')
            return false;
        return opts.includes(a === true || norm(a) === 'true' || norm(a) === 'yes' ? 0 : 1);
    }
    return opts.includes(optionIndex(field, a));
}
const bandOf = (s, score) => (s.bands ?? []).find((b) => score >= Number(b.min) && score <= Number(b.max)) ?? null;
/** The section's highest band (largest min), when it is high severity. */
function severeBand(s) {
    const bands = s.bands ?? [];
    if (!bands.length)
        return null;
    const top = bands.reduce((t, b) => (Number(b.min) > Number(t.min) ? b : t));
    return top.severity === 'high' ? top : null;
}
/** score.ts alertMsgFor: a critical answer first, then the total threshold; '' when neither. */
function alertMessage(s, cfg, answers, score) {
    const crit = (s.alert?.critical ?? []).find((c) => c?.fieldId && criticalHit(c, cfg, answers));
    if (crit)
        return crit.msg || 'Critical item flagged — review immediately.';
    if (s.alert && num(s.alert.min) > 0 && score >= num(s.alert.min))
        return s.alert.msg || 'Elevated risk — flag for clinician review.';
    return '';
}
/** Every section of a submitted form that raises a risk (none when nothing was answered). */
export function evaluateRisk(cfg, answers) {
    const hits = [];
    for (const s of cfg.sections) {
        const ids = (s.fieldIds ?? []).filter((id) => cfg.fields[id]);
        if (!ids.length)
            continue;
        const score = ids.reduce((sum, id) => sum + fieldScore(id, cfg.fields[id], answers), 0);
        const band = bandOf(s, score);
        const message = alertMessage(s, cfg, answers, score);
        const severe = severeBand(s);
        const inSevere = !!severe && score >= Number(severe.min) && score <= Number(severe.max);
        if (!message && !inSevere)
            continue;
        hits.push({
            name: (s.name ?? '').trim(),
            score,
            band: band?.label?.trim() || null,
            message,
        });
    }
    return hits;
}
/** ehr#429 - a field's highest possible points (a matrix: per row). */
export function fieldMax(field) {
    const pos = field.scores.map(num).filter((n) => n > 0);
    switch (field.type) {
        case 'checkboxes':
            return pos.reduce((sum, n) => sum + n, 0);
        case 'radio':
        case 'select':
        case 'toggle':
        case 'checkbox':
        case 'matrix':
            return pos.length ? Math.max(...pos) : 0;
        default: {
            const pts = (field.rules ?? []).map((r) => num(r.points));
            return pts.length ? Math.max(0, ...pts) : 0;
        }
    }
}
/** ehr#429 - did the answers give this field an answer at all (a matrix: any row)? */
export function fieldAnswered(fieldId, field, answers) {
    if (!field)
        return false;
    const a = answers[fieldId];
    if (field.type === 'matrix')
        return ((isObj(a) && Object.keys(a).length > 0) ||
            Object.keys(answers).some((k) => k.startsWith(`${fieldId}::`) && answers[k] !== ''));
    if (Array.isArray(a))
        return a.length > 0;
    return a !== undefined && a !== null && a !== '';
}
/** Each scoring section's score, band and alert for one fill (all of them, not only risks). */
export function sectionScores(cfg, answers) {
    return cfg.sections
        .map((s) => {
        const ids = (s.fieldIds ?? []).filter((id) => cfg.fields[id]);
        const score = ids.reduce((sum, id) => sum + fieldScore(id, cfg.fields[id], answers), 0);
        return {
            name: (s.name ?? '').trim(),
            fieldIds: ids,
            score,
            max: ids.reduce((sum, id) => sum + fieldMax(cfg.fields[id]), 0),
            band: bandOf(s, score),
            message: ids.length ? alertMessage(s, cfg, answers, score) : '',
            answered: ids.some((id) => fieldAnswered(id, cfg.fields[id], answers)),
        };
    })
        .filter((x) => x.fieldIds.length > 0);
}
/** A Form Builder field's scoring facts, from its schema object. */
export function scoredFieldOf(f) {
    const type = String(f.type ?? '');
    const labels = (list) => Array.isArray(list)
        ? list.map((o) => (isObj(o) ? String(o.label ?? o.text ?? '') : String(o ?? '')))
        : [];
    const scores = (list) => (Array.isArray(list) ? list.map(num) : []);
    if (type === 'matrix')
        return {
            type,
            options: labels(f.matrixCols ?? f.cols),
            scores: scores(f.matrixColScores),
        };
    return {
        type,
        options: labels(f.options),
        scores: scores(f.optionScores),
        ...(Array.isArray(f.scoreRules) ? { rules: f.scoreRules } : {}),
    };
}
/**
 * A synced Form Builder form's scoring: the schema's `scoring` (its scoringSections, kept by
 * "Sync from Form Builder") on the schema's own fields. Null when it has none.
 */
export function configFromSchema(schema) {
    if (!isObj(schema) || !Array.isArray(schema.scoring) || !schema.scoring.length)
        return null;
    const fields = {};
    for (const row of Array.isArray(schema.rows) ? schema.rows : []) {
        if (!isObj(row) || !Array.isArray(row.fields))
            continue;
        for (const f of row.fields)
            if (isObj(f) && typeof f.id === 'string')
                fields[f.id] = scoredFieldOf(f);
    }
    const sections = schema.scoring.filter(isObj);
    return sections.length ? { sections, fields } : null;
}
/**
 * A built-in screener's scoring on a form's own field ids: `idOf(item)` names the field that
 * holds item `item` (0-based); an item with no field is left out.
 */
export function configFromBuiltin(b, idOf) {
    const fields = {};
    const ids = [];
    b.items.forEach((it, i) => {
        const id = idOf(i);
        if (!id)
            return;
        ids.push(id);
        fields[id] = { type: 'radio', options: it.options, scores: it.scores };
    });
    const critical = b.critical
        .map((c) => ({ fieldId: idOf(c.item) ?? '', options: c.options, msg: c.msg }))
        .filter((c) => c.fieldId);
    const sections = [
        {
            name: b.name,
            fieldIds: ids,
            bands: b.bands,
            alert: { min: b.alert?.min ?? 0, msg: b.alert?.msg ?? '', critical },
        },
    ];
    for (const s of b.subscales ?? []) {
        const sids = s.items.map((i) => idOf(i)).filter((id) => !!id && !!fields[id]);
        if (sids.length)
            sections.push({
                name: s.name,
                fieldIds: sids,
                bands: s.bands,
                alert: s.alert ? { min: s.alert.min, msg: s.alert.msg } : null,
            });
    }
    return { fields, sections };
}
/**
 * The card's text: the form, each section's score and band, and the alert's own words. No
 * answers or item text beyond what the practice wrote into its alert.
 */
export function riskDesc(formTitle, hits) {
    const title = formTitle.trim() || 'A screener';
    const parts = hits.map((h) => {
        const name = h.name && norm(h.name) !== norm(title) ? `${h.name} ` : '';
        const band = h.band ? ` (${h.band})` : '';
        const msg = h.message ? ` ${h.message.replace(/\s+/g, ' ').trim()}` : '';
        return `${name}score ${h.score}${band}.${msg}`;
    });
    return `${title}: ${parts.join(' ')}`.slice(0, 2000);
}
//# sourceMappingURL=screenerRisk.js.map